import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ModalShell } from "../../atoms/ModalShell";
import { CustomButton } from "../../atoms/CustomButton";
import { CustomInput } from "../../atoms/CustomInput";
import { AlertMessage } from "../../atoms/AlertMessage";
import StepList from "../../atoms/StepList";
import { CopyToClipboardButton } from "../../atoms/CopyToClipboardButton";
import {
  useRequestPassLessLink,
  usePassLessStatus,
  usePassLessServerValidation,
} from "../../../hooks/auth/passwordless";

type Props = {
  show: boolean;
  onClose: () => void;
  defaultEmail?: string;
  /** Supplied by the team-member field on the login screen; absent for owners. */
  entityIdentification?: string;
  onAuthenticated: (userData: any, userEmail: string) => void;
  /** Replaces the dashboard session exchange, for a page that needs another credential. */
  completeWithServerToken?: (target: {
    entity_identification: string;
    user_identification: string;
    token: string;
  }) => Promise<any>;
  namespace?: "translation" | "clientView";
};

/**
 * Passwordless login: the user receives a link by email and retypes the word shown here on the
 * page it opens. The word never travels in the email, which is what makes a forwarded or
 * intercepted link useless on its own.
 */

/**
 * 64 x 64 = 4096 combinations. The size is the point: this word is what stops a forwarded or
 * intercepted link from being usable on its own, and the server allows five attempts, so a smaller
 * list would make guessing realistic. Both lengths are powers of two, so the modulo is unbiased.
 */
// prettier-ignore
const WORDS_LEFT = [
  "AMBER", "AZURE", "BLUE", "BRAVE", "BREEZY", "BRIGHT", "BRONZE", "CALM",
  "CHERRY", "CLEAR", "CLOUDY", "COBALT", "COPPER", "CORAL", "CREAM", "CRISP",
  "DAWN", "EAGER", "FLINT", "FRESH", "FROSTY", "GENTLE", "GOLD", "GRAND",
  "GREEN", "HAZEL", "HONEY", "INDIGO", "IVORY", "JADE", "KEEN", "LEMON",
  "LIGHT", "LILAC", "LIVELY", "LUNAR", "MELLOW", "MERRY", "MINT", "MISTY",
  "NAVY", "NOBLE", "OLIVE", "ONYX", "OPAL", "PEARL", "PLUM", "POLAR",
  "PROUD", "QUIET", "RAPID", "ROSE", "RUBY", "SAND", "SCARLET", "SHARP",
  "SILVER", "SLATE", "STEADY", "SUNNY", "SWIFT", "TEAL", "VIVID", "WARM",
];
// prettier-ignore
const WORDS_RIGHT = [
  "ANCHOR", "ARROW", "ASPEN", "BADGER", "BASIN", "BEACON", "BIRCH", "BOULDER",
  "BRAMBLE", "BRIDGE", "CANYON", "CASCADE", "CAVERN", "CEDAR", "CLOVER", "COMET",
  "COMPASS", "CRATER", "DELTA", "DOLPHIN", "EAGLE", "FALCON", "FOREST", "GARDEN",
  "GLACIER", "GRANITE", "HARVEST", "HERON", "HILLTOP", "ISLAND", "JUNIPER", "LAGOON",
  "LANTERN", "MAPLE", "MARBLE", "MARSH", "MEADOW", "OASIS", "ORCHID", "OSPREY",
  "OTTER", "PEBBLE", "PLATEAU", "POPLAR", "PRAIRIE", "QUARRY", "RAVINE", "RIDGE",
  "RIVER", "SHORE", "SPARROW", "SPRUCE", "STREAM", "SUMMIT", "TERRACE", "THUNDER",
  "TIGER", "TIMBER", "TUNDRA", "VALLEY", "WALNUT", "WILLOW", "WINTER", "ZEPHYR",
];

const buildSecretWord = () => {
  const values = new Uint32Array(2);
  window.crypto.getRandomValues(values);
  const left = WORDS_LEFT[values[0] % WORDS_LEFT.length];
  const right = WORDS_RIGHT[values[1] % WORDS_RIGHT.length];
  return `${left}-${right}`;
};

const formatRemaining = (milliseconds: number) => {
  const total = Math.max(0, Math.ceil(milliseconds / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * The only answer that means "keep waiting". Every other code the status endpoint returns is
 * definitive: UMD has already dropped the request by then, so polling on would hammer the endpoint
 * until the ceiling without ever succeeding, and would never tell the user to start over.
 */
const PENDING_CODE = "RSUSKO022";

/** What to tell the user for each definitive refusal, since the server messages are terse. */
const PASSLESS_ERROR_KEYS: Record<string, string> = {
  RSUSKO015: "passwordless.errorNoAccount",
  RSUSKO016: "passwordless.errorDifferentClient",
  RSUSKO019: "passwordless.errorNoRequest",
  RSUSKO021: "passwordless.expiredHelp",
  RSUSKO023: "passwordless.expiredHelp",
  RSUSKO025: "passwordless.errorWrongWord",
  RSUSKO043: "passwordless.errorTooManyAttempts",
};

const POLL_INTERVAL_MS = 3000;

const POLL_CEILING_MS = 9 * 60 * 1000;
const pollCeilingFrom = (element: any): number => {
  const seconds = Number(element?.poll_ceiling_seconds);
  return Number.isFinite(seconds) && seconds > 0
    ? seconds * 1000
    : POLL_CEILING_MS;
};

export const PassLessLoginModal = ({
  show,
  onClose,
  defaultEmail = "",
  entityIdentification,
  onAuthenticated,
  completeWithServerToken,
  namespace = "translation",
}: Props) => {
  const { t } = useTranslation(namespace);
  const passlessFailure = (error: any) =>
    t(
      PASSLESS_ERROR_KEYS[error?.response?.data?.code] ?? "passwordless.error"
    ) as string;

  const schema = Joi.object({
    user_email: Joi.string()
      .trim()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "any.required": t("validation.emailRequired") as string,
        "string.empty": t("validation.emailRequired") as string,
        "string.email": t("validation.emailInvalid") as string,
      }),
  }).required();

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: { user_email: defaultEmail },
  });

  const [email, setEmail] = useState(defaultEmail);
  const [secretWord, setSecretWord] = useState(buildSecretWord);
  const [sent, setSent] = useState(false);
  const [expired, setExpired] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [scope, setScope] = useState<{
    entity_identification: string;
    user_identification: string;
  } | null>(null);

  const [remainingMs, setRemainingMs] = useState(POLL_CEILING_MS);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadline = useRef<number>(0);

  const { mutate: requestLink, isLoading: isSending } =
    useRequestPassLessLink();
  const { mutateAsync: pollStatus } = usePassLessStatus();
  const { mutateAsync: validateServerToken } = usePassLessServerValidation();

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    setSent(false);
    setExpired(false);
    setFailureMessage(null);
    setScope(null);
    setRemainingMs(POLL_CEILING_MS);
    setSecretWord(buildSecretWord());
  }, [stopPolling]);

  useEffect(() => {
    if (show) {
      setEmail(defaultEmail);
      resetForm({ user_email: defaultEmail });
    } else {
      reset();
    }
  }, [show, defaultEmail, reset, resetForm]);

  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    if (!sent || expired) {
      return;
    }
    const tick = setInterval(
      () => setRemainingMs(Math.max(0, deadline.current - Date.now())),
      1000
    );
    return () => clearInterval(tick);
  }, [sent, expired]);

  const poll = useCallback(async () => {
    if (!scope) {
      return;
    }
    if (Date.now() > deadline.current) {
      stopPolling();
      setExpired(true);
      return;
    }

    try {
      const statusResponse = await pollStatus({
        ...scope,
        secret_word: secretWord,
      });
      const serverToken =
        statusResponse?.data?.element?.server_validation_token;

      if (!serverToken) {
        pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }

      stopPolling();
      if (completeWithServerToken) {
        let result;
        try {
          result = await completeWithServerToken({
            ...scope,
            token: serverToken,
          });
        } catch (exchangeError: any) {
          setFailureMessage(passlessFailure(exchangeError));
          setExpired(true);
          return;
        }
        onAuthenticated(result, email);
        return;
      }
      const sessionResponse = await validateServerToken({
        ...scope,
        token: serverToken,
      });
      const userData = sessionResponse?.data?.element;

      if (!userData?.access_token) {
        setExpired(true);
        return;
      }
      onAuthenticated(userData, email);
    } catch (error: any) {
      const code = error?.response?.data?.code;

      // A transport error carries no code: a lost packet must not end a login the user can still
      // complete, so it is retried like a pending answer.
      if (!code || code === PENDING_CODE) {
        pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }

      stopPolling();
      setFailureMessage(passlessFailure(error));
      setExpired(true);
    }
  }, [
    scope,
    secretWord,
    email,
    pollStatus,
    validateServerToken,
    completeWithServerToken,
    onAuthenticated,
    stopPolling,
    t,
  ]);

  useEffect(() => {
    if (sent && scope) {
      pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
    }
    return stopPolling;
    // `poll` is rebuilt on every render it depends on; re-arming here would restart the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent, scope]);

  const onSend = handleSubmit(({ user_email }) => {
    const trimmedEmail = user_email.trim();
    setEmail(trimmedEmail);
    const scopedEntity = entityIdentification?.trim() || undefined;

    requestLink(
      {
        user_email: trimmedEmail,
        secret_word: secretWord,
        entity_identification: scopedEntity,
      },
      {
        onSuccess: (response) => {
          const element = response?.data?.element;
          const entity = scopedEntity || element?.entity_identification;
          if (!element?.user_identification || !entity) {
            toast.error(t("passwordless.error"));
            return;
          }
          const ceiling = pollCeilingFrom(element);
          deadline.current = Date.now() + ceiling;
          setRemainingMs(ceiling);
          setScope({
            entity_identification: entity,
            user_identification: element.user_identification,
          });
          setSent(true);
        },
        onError: (error: any) => {
          toast.error(passlessFailure(error));
        },
      }
    );
  });

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="md"
      title={t("passwordless.title")}
      subtitle={
        sent ? t("passwordless.sentSubtitle") : t("passwordless.subtitle")
      }
    >
      {!sent ? (
        <div className="flex flex-col gap-4">
          <CustomInput
            name="user_email"
            control={control}
            type="email"
            label={t("login.label_1") as string}
            error={errors.user_email}
            helperText={errors.user_email?.message}
            placeholder="name@company.com"
          />
          <CustomButton
            variant="primary"
            size="md"
            title={t("passwordless.sendBtn")}
            isLoading={isSending}
            disabled={isSending}
            onClick={onSend}
          />
        </div>
      ) : expired ? (
        <div className="flex flex-col gap-4">
          <AlertMessage color="warning">
            {failureMessage || t("passwordless.expiredHelp")}
          </AlertMessage>
          <CustomButton
            variant="primary"
            size="md"
            title={t("passwordless.retryBtn")}
            onClick={reset}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-gray-700">
            {t("passwordless.sentTo")}{" "}
            <span className="font-medium text-gray-900">{email}</span>
          </p>

          <div className="border-t border-gray-200 pt-4">
            <p className="text-xs text-gray-500">
              {t("passwordless.wordLabel")}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <p className="text-2xl font-semibold tracking-wide text-gray-900">
                {secretWord}
              </p>
              <CopyToClipboardButton
                translationNamespace="clientView"
                textToCopy={secretWord}
                variant="secondary"
                size="sm"
                fullWidth={false}
                label={t("passwordless.copyWord")}
              />
            </div>
          </div>

          <StepList
            className="border-t border-gray-200 pt-4"
            steps={[
              { status: "done", title: t("passwordless.step1Done") },
              { status: "current", marker: 1, title: t("passwordless.step2") },
              { status: "next", marker: 2, title: t("passwordless.step3") },
            ]}
          />

          <div className="flex flex-col gap-1 border-t border-gray-200 pt-4">
            <p className="flex items-center gap-2 text-sm text-gray-700">
              <span
                className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-blue-600"
                aria-hidden="true"
              />
              {t("passwordless.waiting")}
              <span className="ml-auto tabular-nums text-gray-500">
                {formatRemaining(remainingMs)}
              </span>
            </p>
            <p className="text-xs text-gray-500">
              {t("passwordless.waitingHelp")}
            </p>
            <p className="text-xs text-gray-500">
              {t("passwordless.spamHint")}
            </p>
          </div>
        </div>
      )}
    </ModalShell>
  );
};

export default PassLessLoginModal;
